import { useCallback, useEffect } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Popover } from '@lyearn/ui';
import { getTagFilterOptionWithConfig } from '@/components/FilterManager/configs/tags';
import EntityDefaultChildren from '@/components/FilterManager/EntityFilter/components/EntityDefaultChildren';
import FilterPillLoader from '@/components/FilterManager/EntityFilter/components/FilterPillLoader';
import { getSelectedOperatorTillBESupport } from '@/components/FilterManager/SortAndFilter/components/SortAndFilter';
import { getSelectedTagValuesNew } from '@/components/FilterManager/SortAndFilter/components/TagsFilterContainer/utils';
import { FilterBodyRendererProps } from '@/components/Filters/types';
import { useTagLiteQuery } from '@/modules/corporate/modules/tags/views/TagsEditor/graphql/queries/generated/tagLite';
import { EntityTagInput, TagTypeEnum } from '@/types/schema';

import { FilterBodyRenderer } from '../../FilterBodyRenderer';
import { TagFilterContainerProps, TagFilterProps } from './types';
import { valueFieldName } from './useTagStateAndMethods';

const TagFilter = ({
  tagConfig,
  tagFilterType,
  onTagTypeFilterChange,
  onRemoveTag,
  selectedTag,
  isOpen,
  onToggle,
  isNonEditable,
}: TagFilterProps) => {
  const { isMobile } = useScreen();
  const { t } = useTranslation();
  const {
    configToRenderFilterBody: {
      listConfig: { useFilterValues },
    },
  } = tagConfig;

  const getSelectedTagValue = (tagFilterType: EntityTagInput) => {
    const type = tagFilterType.type;
    if (!type || !tagFilterType) return [];
    const value = tagFilterType[valueFieldName[type]];

    return [TagTypeEnum.User, TagTypeEnum.MultiSelect, TagTypeEnum.SingleSelect].includes(
      selectedTag.type,
    )
      ? (value as string[])
      : getSelectedTagValuesNew(selectedTag, tagFilterType);
  };

  const { selectedOptions, fetching, resolvedValue } = useFilterValues({
    value: getSelectedTagValue(tagFilterType),
    queryParams: { tagId: tagFilterType.tagId },
  });

  const onChange: FilterBodyRendererProps['onChange'] = useCallback(
    (id, items) => {
      switch (selectedTag.type) {
        case TagTypeEnum.Text:
          onTagTypeFilterChange(items[0].value, selectedTag);
          break;
        case TagTypeEnum.Number:
          onTagTypeFilterChange(Number(items[0].value), selectedTag);
          break;
        case TagTypeEnum.Date:
          onTagTypeFilterChange(JSON.parse(items[0].value), selectedTag);
          break;
        default:
          onTagTypeFilterChange(
            items.map((i) => i.value),
            selectedTag,
          );
      }
      onToggle();
    },
    [onTagTypeFilterChange, onToggle, selectedTag],
  );

  return (
    <Popover key={tagConfig.value} open={isOpen} onOpenChange={onToggle}>
      <Popover.Button>
        <EntityDefaultChildren
          defaultPlaceholder={selectedTag.name}
          isNonEditable={isNonEditable}
          selectedOperator={getSelectedOperatorTillBESupport(t)}
          selectedValues={resolvedValue}
          onRemove={onRemoveTag ? () => onRemoveTag(selectedTag.id) : undefined}
        />
      </Popover.Button>
      <Popover.Content position="bottom-start" renderAs={isMobile ? 'bottom-sheet' : 'popover'}>
        <FilterBodyRenderer
          selectedPathConfig={{
            ...tagConfig.configToRenderFilterBody,
            headerProps: {},
            // @ts-ignore
            initialSelectedOptions: [
              TagTypeEnum.User,
              TagTypeEnum.MultiSelect,
              TagTypeEnum.SingleSelect,
            ].includes(selectedTag.type)
              ? selectedOptions
              : tagFilterType.dateValue ??
                tagFilterType.numberValue ??
                tagFilterType.textValue ??
                [],
          }}
          onChange={onChange}
        />
      </Popover.Content>
    </Popover>
  );
};

const TagFilterContainer = ({
  tagFilterType,
  onTagTypeFilterChange,
  onRemoveTag,
  filterIdToOpen,
  setFilterIdToOpen,
  isNonEditableIds,
}: TagFilterContainerProps) => {
  const { t } = useTranslation('Tags');
  const [isOpen, { toggle }] = useToggle(false);
  const [{ fetching, data }] = useTagLiteQuery({
    variables: { filters: { tagId: tagFilterType.tagId } },
  });

  useEffect(() => {
    if (filterIdToOpen === tagFilterType.tagId && !isNonEditableIds?.includes(filterIdToOpen)) {
      toggle(true);
    } else {
      toggle(false);
    }
  }, [filterIdToOpen, isNonEditableIds, tagFilterType.tagId, toggle]);

  if (fetching || !data?.tag) {
    return <FilterPillLoader className="mb-8 me-8" />;
  }

  const tagConfig = getTagFilterOptionWithConfig(t, data.tag);

  return (
    <TagFilter
      isNonEditable={isNonEditableIds?.includes(tagFilterType.tagId)}
      isOpen={isOpen}
      selectedTag={data.tag}
      tagConfig={tagConfig}
      tagFilterType={tagFilterType}
      onRemoveTag={onRemoveTag}
      onTagTypeFilterChange={onTagTypeFilterChange}
      onToggle={(open) => {
        toggle();
        setFilterIdToOpen(open ? tagFilterType.tagId : '');
      }}
    />
  );
};

export default TagFilterContainer;
